
import React from 'react';
import { useFoodSearch } from './useFoodSearch';
import SearchBar from './SearchBar';
import SearchResults from './SearchResults';
import SelectedFood from './SelectedFood';
import CustomFoodForm from './CustomFoodForm';
import { FoodSearchProps } from './types';

const FoodSearch: React.FC<FoodSearchProps> = ({ onAddFood }) => {
  const {
    searchTerm,
    searchResults,
    loading,
    selectedFood,
    amount,
    dataLoadError,
    lastSearched,
    foodDatabaseStats,
    currentPage,
    resultsPerPage,
    totalPages,
    paginatedResults,
    showCustomFoodForm,
    customFood,
    handleSearch,
    handleSelectFood,
    handleAddFood,
    handleClearSelection,
    handlePageChange,
    setShowCustomFoodForm,
    setAmount,
    handleCustomFoodChange,
    handleAddCustomFood,
    handleDeleteCustomFood
  } = useFoodSearch(onAddFood);

  return (
    <div className="mt-6 mb-8">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-t-xl p-6 border border-green-100">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <span className="text-white text-xl">🔍</span>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Buscador de Alimentos</h3>
            <p className="text-sm text-gray-600">Encuentra y añade alimentos a tu menú diario</p>
          </div>
        </div>
        
        {dataLoadError && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-amber-600">⚠️</span>
              <span className="text-sm text-amber-800">
                Usando base de datos local. Algunos alimentos podrían no estar disponibles.
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Search content */}
      <div className="bg-white rounded-b-xl border-x border-b border-green-100 p-6 space-y-4">
        <SearchBar 
          searchTerm={searchTerm}
          onSearchChange={handleSearch}
          loading={loading}
          lastSearched={lastSearched}
          onSearchTermClick={handleSearch}
          onShowCustomForm={() => setShowCustomFoodForm(true)}
          showCustomForm={showCustomFoodForm}
          showSelectedFood={!!selectedFood}
        />
        
        <SearchResults 
          paginatedResults={paginatedResults}
          searchTerm={searchTerm}
          loading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          searchResults={searchResults}
          resultsPerPage={resultsPerPage}
          onSelectFood={handleSelectFood}
          onPageChange={handlePageChange}
          onShowCustomFoodForm={() => setShowCustomFoodForm(true)}
          onDeleteCustomFood={handleDeleteCustomFood}
        />
        
        {/* Custom food form */}
        {showCustomFoodForm && (
          <CustomFoodForm 
            customFood={customFood}
            onCustomFoodChange={handleCustomFoodChange}
            onAddCustomFood={handleAddCustomFood}
            onCancel={() => setShowCustomFoodForm(false)}
          />
        )}

        {/* Selected food */}
        {selectedFood && (
          <SelectedFood 
            selectedFood={selectedFood}
            amount={amount}
            onAmountChange={setAmount}
            onAddFood={handleAddFood}
            onClearSelection={handleClearSelection}
          />
        )}
        
        {/* Database statistics */}
        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-gray-500">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              <span>Base de datos: {foodDatabaseStats.total} alimentos</span>
            </div>
            <div className="flex items-center gap-4 text-gray-400">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                {foodDatabaseStats.usda} USDA
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                {foodDatabaseStats.openFoodFacts} españoles
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodSearch;
